import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiUsers, FiBookOpen,
  FiUser, FiBriefcase
} from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import AccountSelect from '../../components/AccountSelect';
import { MasterHeaderActions, MasterAddButton, ContentCard } from '../../components/SharedStyles';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import useCurrency from '../../hooks/useCurrency';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import useEscapeClose from '../../hooks/useEscapeClose';
import usePagePermissions from '../../hooks/usePagePermissions';
import {
  findParentAccountForEntity, generateAccountCode,
  createLinkedAccount, getLinkedAccount
} from '../../utils/accountHelper';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { isValidEmail, isValidPhone, writeAuditLog } from '../../utils/validationHelpers';
import { SYSTEM_ACCOUNTS } from '../../config/tranTypes';

const EMPLOYMENT_STATUSES = ['Probation', 'Permanent', 'Contract', 'Consultant'];

const EMPTY_EARNING = { name: '', amountType: 'fixed', value: 0 };
const EMPTY_DEDUCTION = { name: '', amountType: 'fixed', value: 0, accountId: '', accountCode: '', accountName: '' };

const SalaryStaff = () => {
  const navigate = useNavigate();
  const { activeCompany, userProfile } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('salary', 'salaryStaff');
  const { formatCurrency } = useCurrency();

  const [staff, setStaff] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');

  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const blankForm = {
    staffCode: '',
    fullName: '', cnic: '',
    email: '', phone: '',
    address: '', city: '',
    designation: '', department: '', grade: '',
    joiningDate: new Date().toISOString().split('T')[0],
    employmentStatus: 'Permanent',
    linkedEmployeeId: '',
    bankName: '', bankAccountNo: '', iban: '',
    ntn: '', eobiNumber: '', sessiNumber: '', pfNumber: '',
    basicSalary: 0,
    otRatePerHour: 0,
    notes: '',
    isActive: true
  };

  const [formData, setFormData] = useState(blankForm);
  const [earnings, setEarnings] = useState([]);
  const [deductions, setDeductions] = useState([]);

  // ─── Load data ───
  const fetchStaff = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'companies', activeCompany.id, 'salaryStaff'),
        orderBy('staffCode')
      ));
      setStaff(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('Load staff error:', e); }
    finally { setLoading(false); }
  }, [activeCompany?.id]);

  const fetchEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const empRows = await getEmployeesCached(activeCompany.id);
      setEmployees(empRows.filter(r => r.isActive === true));
    } catch (e) { console.error('Load employees error:', e); }
  }, [activeCompany?.id]);

  useEffect(() => { fetchStaff(); fetchEmployees(); }, [fetchStaff, fetchEmployees]);

  // ─── Code generator ───
  const generateStaffCode = () => {
    const max = staff.reduce((m, s) => {
      const n = parseInt((s.staffCode || '').replace(/\D/g, '')) || 0;
      return n > m ? n : m;
    }, 0);
    return `STF-${String(max + 1).padStart(4, '0')}`;
  };

  // ─── Modal handlers ───
  const openNew = () => {
    setEditing(null);
    setFormData({ ...blankForm, staffCode: generateStaffCode() });
    setEarnings([]);
    setDeductions([]);
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setFormData({
      staffCode: s.staffCode || '',
      fullName: s.fullName || '',
      cnic: s.cnic || '',
      email: s.email || '',
      phone: s.phone || '',
      address: s.address || '',
      city: s.city || '',
      designation: s.designation || '',
      department: s.department || '',
      grade: s.grade || '',
      joiningDate: s.joiningDate || '',
      employmentStatus: s.employmentStatus || 'Permanent',
      linkedEmployeeId: s.linkedEmployeeId || '',
      bankName: s.bankName || '',
      bankAccountNo: s.bankAccountNo || '',
      iban: s.iban || '',
      ntn: s.ntn || '',
      eobiNumber: s.eobiNumber || '',
      sessiNumber: s.sessiNumber || '',
      pfNumber: s.pfNumber || '',
      basicSalary: s.basicSalary || 0,
      otRatePerHour: s.otRatePerHour || 0,
      notes: s.notes || '',
      isActive: s.isActive !== false
    });
    setEarnings((s.earnings || []).map(e => ({ ...EMPTY_EARNING, ...e })));
    setDeductions((s.deductions || []).map(d => ({ ...EMPTY_DEDUCTION, ...d })));
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  // ─── Earnings / Deductions row handlers ───
  const updateEarning = (i, patch) => setEarnings(prev => prev.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const addEarning = () => setEarnings(prev => [...prev, { ...EMPTY_EARNING }]);
  const removeEarning = (i) => setEarnings(prev => prev.filter((_, idx) => idx !== i));

  const updateDeduction = (i, patch) => setDeductions(prev => prev.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const addDeduction = () => setDeductions(prev => [...prev, { ...EMPTY_DEDUCTION }]);
  const removeDeduction = (i) => setDeductions(prev => prev.filter((_, idx) => idx !== i));

  const handleDeductionAccount = (i, account) => updateDeduction(i, {
    accountId: account?.id || '',
    accountCode: account?.code || '',
    accountName: account?.name || ''
  });

  // ─── Computed gross (live preview) ───
  const computeGross = () => {
    const basic = Number(formData.basicSalary) || 0;
    const earningsTotal = earnings.reduce((sum, e) => {
      const v = Number(e.value) || 0;
      return sum + (e.amountType === 'percent' ? (basic * v / 100) : v);
    }, 0);
    return basic + earningsTotal;
  };

  const computeDeductions = () => {
    const basic = Number(formData.basicSalary) || 0;
    return deductions.reduce((sum, d) => {
      const v = Number(d.value) || 0;
      return sum + (d.amountType === 'percent' ? (basic * v / 100) : v);
    }, 0);
  };

  // ─── Validation ───
  const validate = () => {
    if (!formData.fullName.trim()) return 'Full name is required.';
    if (formData.email && !isValidEmail(formData.email)) return 'Invalid email format.';
    if (formData.phone && !isValidPhone(formData.phone)) return 'Invalid phone format.';
    if (Number(formData.basicSalary) < 0) return 'Basic salary cannot be negative.';
    const dup = staff.find(s =>
      s.id !== editing?.id &&
      (s.fullName || '').trim().toLowerCase() === formData.fullName.trim().toLowerCase()
    );
    if (dup) return `A staff member with name "${formData.fullName}" already exists (${dup.staffCode}).`;
    for (let i = 0; i < earnings.length; i++) {
      const e = earnings[i];
      if (!e.name?.trim()) return `Earning row ${i + 1}: name is required.`;
      if (Number(e.value) < 0) return `Earning row ${i + 1}: value cannot be negative.`;
    }
    for (let i = 0; i < deductions.length; i++) {
      const d = deductions[i];
      if (!d.name?.trim()) return `Deduction row ${i + 1}: name is required.`;
      if (Number(d.value) < 0) return `Deduction row ${i + 1}: value cannot be negative.`;
      if (!d.accountId) return `Deduction row ${i + 1}: select a GL account.`;
    }
    return null;
  };

  // ─── Auto-create linked COA account ───
  const ensureLinkedAccount = async (staffId, fullName) => {
    let linked = await getLinkedAccount(activeCompany.id, 'staff', staffId);
    if (linked) {
      if (linked.name !== `Staff: ${fullName}`) {
        await updateDoc(doc(db, 'companies', activeCompany.id, 'accounts', linked.id), {
          name: `Staff: ${fullName}`,
          updatedAt: new Date().toISOString()
        });
      }
      return linked.id;
    }
    const parent = await findParentAccountForEntity(activeCompany.id, 'staff');
    if (!parent) {
      throw new Error(
        `Cannot create staff account: parent "Staff Accounts (${SYSTEM_ACCOUNTS.STAFF_ACCOUNTS_PARENT})" not found in COA. ` +
        `Go to Master Data → Chart of Accounts → "Repair COA" first.`
      );
    }
    const newCode = await generateAccountCode(activeCompany.id, parent.id, parent.code, parent.level);
    return await createLinkedAccount(
      activeCompany.id, 'staff', staffId, `Staff: ${fullName}`,
      newCode, parent.id, 0, userProfile?.uid
    );
  };

  // ─── Save ───
  const handleSave = async () => {
    if (!canEdit) { alert('You do not have permission to save.'); return; }
    const err = validate();
    if (err) { alert(err); return; }
    setSaving(true);
    try {
      const cleanEarnings = earnings.map(e => ({ name: e.name.trim(), amountType: e.amountType, value: Number(e.value) || 0 }));
      const cleanDeductions = deductions.map(d => ({
        name: d.name.trim(), amountType: d.amountType, value: Number(d.value) || 0,
        accountId: d.accountId, accountCode: d.accountCode, accountName: d.accountName
      }));

      const payload = {
        staffCode: formData.staffCode,
        fullName: formData.fullName.trim(),
        cnic: formData.cnic.trim(),
        email: formData.email.trim(), phone: formData.phone.trim(),
        address: formData.address.trim(), city: formData.city.trim(),
        designation: formData.designation.trim(),
        department: formData.department.trim(),
        grade: formData.grade.trim(),
        joiningDate: formData.joiningDate,
        employmentStatus: formData.employmentStatus,
        linkedEmployeeId: formData.linkedEmployeeId || null,
        bankName: formData.bankName.trim(),
        bankAccountNo: formData.bankAccountNo.trim(),
        iban: formData.iban.trim().toUpperCase(),
        ntn: formData.ntn.trim(),
        eobiNumber: formData.eobiNumber.trim(),
        sessiNumber: formData.sessiNumber.trim(),
        pfNumber: formData.pfNumber.trim(),
        basicSalary: Number(formData.basicSalary) || 0,
        otRatePerHour: Number(formData.otRatePerHour) || 0,
        earnings: cleanEarnings,
        deductions: cleanDeductions,
        notes: formData.notes.trim(),
        isActive: !!formData.isActive,
        grossSalary: computeGross(),
        deductionsTotal: computeDeductions(),
        netSalary: computeGross() - computeDeductions(),
        updatedAt: serverTimestamp(),
        updatedBy: userProfile?.uid || null
      };

      let staffId;
      if (editing) {
        await updateDoc(doc(db, 'companies', activeCompany.id, 'salaryStaff', editing.id), payload);
        staffId = editing.id;

        // If staff has linked account, update its name if fullName changed
        if (editing.linkedAccountId && editing.fullName !== payload.fullName) {
          try {
            const linkedAccountRef = doc(db, 'companies', activeCompany.id, 'accounts', editing.linkedAccountId);
            await updateDoc(linkedAccountRef, {
              name: `Staff: ${payload.fullName}`,
              updatedAt: new Date().toISOString()
            });
          } catch (accountError) {
            console.error('Failed to update linked staff account name:', accountError);
          }
        }
      } else {
        payload.createdAt = serverTimestamp();
        payload.createdBy = userProfile?.uid || null;
        payload.linkedAccountId = null;
        const ref = await addDoc(collection(db, 'companies', activeCompany.id, 'salaryStaff'), payload);
        staffId = ref.id;
      }

      const linkedAccountId = await ensureLinkedAccount(staffId, payload.fullName);
      await updateDoc(doc(db, 'companies', activeCompany.id, 'salaryStaff', staffId), { linkedAccountId });

      setShowModal(false);
      fetchStaff();
    } catch (e) {
      console.error('Save staff error:', e);
      alert('Failed to save: ' + (e?.message || ''));
    } finally { setSaving(false); }
  };

  const handleDelete = async (s) => {
    if (!canDelete) { alert('No permission to delete.'); return; }
    if (!window.confirm(`Delete salary staff "${s.fullName}"? Linked COA account will also be removed if no transactions exist.`)) return;
    try {
      const { hasReferences, message } = await checkEntityReferences(
        activeCompany.id, 'staff', s.id, { linkedAccountId: s.linkedAccountId }
      );
      if (hasReferences) { alert(message); return; }
      await deleteDoc(doc(db, 'companies', activeCompany.id, 'salaryStaff', s.id));
      if (s.linkedAccountId) {
        try { await deleteDoc(doc(db, 'companies', activeCompany.id, 'accounts', s.linkedAccountId)); }
        catch (acErr) { console.warn('Could not delete linked account:', acErr); }
      }
      writeAuditLog(activeCompany.id, userProfile?.uid, 'delete', 'salaryStaff', s.id, { name: s.fullName });
      fetchStaff();
    } catch (e) { alert('Failed to delete: ' + (e?.message || '')); }
  };

  const openLedger = (s) => {
    if (!s.linkedAccountId) {
      alert('This staff has no linked account yet. Edit and re-save to create one.');
      return;
    }
    navigate(`/reports/ledger?accountId=${s.linkedAccountId}`);
  };

  useDocumentShortcuts({
    onNew: openNew,
    onSave: handleSave,
    viewMode: showModal ? 'form' : 'list',
    saving
  });

  // ─── Filtering ───
  const departments = [...new Set(staff.map(s => s.department).filter(Boolean))].sort();

  const filtered = staff.filter(s => {
    if (statusFilter === 'active' && s.isActive === false) return false;
    if (statusFilter === 'inactive' && s.isActive !== false) return false;
    if (departmentFilter !== 'all' && s.department !== departmentFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const m = (s.staffCode || '').toLowerCase().includes(q) ||
                (s.fullName || '').toLowerCase().includes(q) ||
                (s.cnic || '').toLowerCase().includes(q) ||
                (s.designation || '').toLowerCase().includes(q);
      if (!m) return false;
    }
    return true;
  });

  // ═══════════════════════════════════════════════════════════
  return (
    <PageLayout pageTitle="Salary Staff" breadcrumb="Salary / Salary Staff">
      <MasterHeaderActions>
        {canEdit && (
          <MasterAddButton onClick={openNew}>
            <FiPlus /> Add Salary Staff
          </MasterAddButton>
        )}
      </MasterHeaderActions>

      <ContentCard>
        <SearchBar>
          <SearchInput>
            <FiSearch />
            <input
              type="text"
              placeholder="Search by code, name, CNIC, or designation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchInput>
          <FilterGroup>
            <FilterSelect value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
              <option value="all">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </FilterSelect>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
              <option value="all">All</option>
            </FilterSelect>
          </FilterGroup>
        </SearchBar>

        {loading ? (
          <LoadingState>Loading salary staff...</LoadingState>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <FiUsers />
            <p>{staff.length === 0 ? 'No salary staff added yet' : 'No staff match your filters'}</p>
            <small>{staff.length === 0 ? 'Click "Add Salary Staff" to get started' : ''}</small>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Designation</Th>
                <Th>Department</Th>
                <Th>Joining</Th>
                <Th>Status</Th>
                <Th style={{ textAlign: 'right' }}>Basic</Th>
                <Th style={{ textAlign: 'right' }}>Gross</Th>
                <Th style={{ textAlign: 'right' }}>Net</Th>
                <Th>Active</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <Tr key={s.id}>
                  <Td><strong>{s.staffCode}</strong></Td>
                  <Td>
                    <StaffInfo>
                      <StaffName>{s.fullName}</StaffName>
                      {s.cnic && <SubText>CNIC: {s.cnic}</SubText>}
                    </StaffInfo>
                  </Td>
                  <Td>{s.designation || '-'}</Td>
                  <Td>{s.department || '-'}</Td>
                  <Td>{s.joiningDate || '-'}</Td>
                  <Td><EmpBadge $status={s.employmentStatus}>{s.employmentStatus || '-'}</EmpBadge></Td>
                  <Td style={{ textAlign: 'right' }}>{formatCurrency(s.basicSalary || 0)}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatCurrency(s.grossSalary || 0)}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(s.netSalary || 0)}</Td>
                  <Td><StatusBadge $active={s.isActive !== false}>{s.isActive !== false ? 'Active' : 'Inactive'}</StatusBadge></Td>
                  <Td>
                    <ActionButtons>
                      <IconButton onClick={() => openLedger(s)} title="Open Ledger"><FiBookOpen /></IconButton>
                      {canEdit && <IconButton onClick={() => openEdit(s)} title="Edit"><FiEdit2 /></IconButton>}
                      {canDelete && <IconButton onClick={() => handleDelete(s)} title="Delete" $danger><FiTrash2 /></IconButton>}
                    </ActionButtons>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </ContentCard>

      {/* ───────── Modal ───────── */}
      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FiUser /> {editing ? `Edit ${editing.staffCode} — ${editing.fullName}` : 'New Salary Staff'}
              </ModalTitle>
              <CloseButton onClick={closeModal}><FiX /></CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* Personal */}
              <SectionTitle>Personal Info</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Staff Code</Label>
                  <Input value={formData.staffCode} disabled />
                </FormGroup>
                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} autoFocus />
                </FormGroup>
                <FormGroup>
                  <Label>CNIC</Label>
                  <Input value={formData.cnic} onChange={e => setFormData({...formData, cnic: e.target.value})} placeholder="12345-1234567-1" />
                </FormGroup>
                <FormGroup>
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>City</Label>
                  <Input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
                </FormGroup>
                <FormGroup $full>
                  <Label>Address</Label>
                  <Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
                </FormGroup>
              </FormGrid>

              {/* HR */}
              <SectionTitle>HR Details</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Designation</Label>
                  <Input value={formData.designation} onChange={e => setFormData({...formData, designation: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>Department</Label>
                  <Input value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} placeholder="e.g. Sales, Accounts" />
                </FormGroup>
                <FormGroup>
                  <Label>Grade</Label>
                  <Input value={formData.grade} onChange={e => setFormData({...formData, grade: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>Joining Date</Label>
                  <Input type="date" value={formData.joiningDate} onChange={e => setFormData({...formData, joiningDate: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>Employment Status</Label>
                  <Select value={formData.employmentStatus} onChange={e => setFormData({...formData, employmentStatus: e.target.value})}>
                    {EMPLOYMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Linked Operational Employee (optional)</Label>
                  <Select value={formData.linkedEmployeeId} onChange={e => setFormData({...formData, linkedEmployeeId: e.target.value})}>
                    <option value="">— None —</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeCode})</option>)}
                  </Select>
                </FormGroup>
                <FormGroup $full>
                  <CheckboxWrapper>
                    <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({...formData, isActive: e.target.checked})} />
                    Active
                  </CheckboxWrapper>
                </FormGroup>
              </FormGrid>

              {/* Bank */}
              <SectionTitle>Bank for Salary Deposit</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Bank Name</Label>
                  <Input value={formData.bankName} onChange={e => setFormData({...formData, bankName: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>Account No</Label>
                  <Input value={formData.bankAccountNo} onChange={e => setFormData({...formData, bankAccountNo: e.target.value})} />
                </FormGroup>
                <FormGroup $full>
                  <Label>IBAN</Label>
                  <Input value={formData.iban} onChange={e => setFormData({...formData, iban: e.target.value})} placeholder="PK00ABCD0123456789012345" />
                </FormGroup>
              </FormGrid>

              {/* Statutory IDs */}
              <SectionTitle>Statutory IDs (for reporting)</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>NTN (Income Tax)</Label>
                  <Input value={formData.ntn} onChange={e => setFormData({...formData, ntn: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>EOBI #</Label>
                  <Input value={formData.eobiNumber} onChange={e => setFormData({...formData, eobiNumber: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>SESSI #</Label>
                  <Input value={formData.sessiNumber} onChange={e => setFormData({...formData, sessiNumber: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>Provident Fund #</Label>
                  <Input value={formData.pfNumber} onChange={e => setFormData({...formData, pfNumber: e.target.value})} />
                </FormGroup>
              </FormGrid>

              {/* Salary Structure */}
              <SectionTitle>Salary Structure</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Basic Salary *</Label>
                  <Input type="number" min="0" step="0.01"
                    value={formData.basicSalary}
                    onChange={e => setFormData({...formData, basicSalary: e.target.value})} />
                </FormGroup>
                <FormGroup>
                  <Label>OT Rate / Hour</Label>
                  <Input type="number" min="0" step="0.01"
                    value={formData.otRatePerHour}
                    onChange={e => setFormData({...formData, otRatePerHour: e.target.value})}
                    title="Fixed overtime pay per hour. Used when a payroll run's OT Method is set to Fixed Per-Hour. Leave 0 if you use the By-Salary method." />
                </FormGroup>
              </FormGrid>

              {/* Allowances */}
              <SubHeader>
                <SubLabel>Allowances (Earnings)</SubLabel>
                <SubAddBtn onClick={addEarning}><FiPlus /> Add</SubAddBtn>
              </SubHeader>
              {earnings.length === 0 ? (
                <EmptyHint>No allowances. Click Add to include HRA, Medical, Conveyance, etc.</EmptyHint>
              ) : (
                <RowTableWrap>
                  <RowTable>
                    <thead><tr>
                      <RTh style={{ width: 36 }}>#</RTh>
                      <RTh>Name</RTh>
                      <RTh style={{ width: 130 }}>Type</RTh>
                      <RTh style={{ width: 140, textAlign: 'right' }}>Value</RTh>
                      <RTh style={{ width: 140, textAlign: 'right' }}>Computed</RTh>
                      <RTh style={{ width: 40 }}></RTh>
                    </tr></thead>
                    <tbody>
                      {earnings.map((e, i) => {
                        const computed = e.amountType === 'percent' ? (Number(formData.basicSalary) || 0) * (Number(e.value) || 0) / 100 : Number(e.value) || 0;
                        return (
                          <tr key={i}>
                            <RTd>{i + 1}</RTd>
                            <RTd><RInp value={e.name} onChange={ev => updateEarning(i, { name: ev.target.value })} placeholder="HRA, Medical..." /></RTd>
                            <RTd><RSel value={e.amountType} onChange={ev => updateEarning(i, { amountType: ev.target.value })}>
                              <option value="fixed">Fixed Rs</option>
                              <option value="percent">% of Basic</option>
                            </RSel></RTd>
                            <RTd><RInp type="number" min="0" step="0.01" value={e.value} onChange={ev => updateEarning(i, { value: ev.target.value })} style={{ textAlign: 'right' }} /></RTd>
                            <RTd style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(computed)}</RTd>
                            <RTd style={{ textAlign: 'center' }}><RmBtn onClick={() => removeEarning(i)}><FiX /></RmBtn></RTd>
                          </tr>
                        );
                      })}
                    </tbody>
                  </RowTable>
                </RowTableWrap>
              )}

              {/* Deductions */}
              <SubHeader>
                <SubLabel>Deductions</SubLabel>
                <SubAddBtn onClick={addDeduction}><FiPlus /> Add</SubAddBtn>
              </SubHeader>
              {deductions.length === 0 ? (
                <EmptyHint>No deductions. Click Add for EOBI, Income Tax, Provident Fund, etc.</EmptyHint>
              ) : (
                <RowTableWrapWide>
                  <RowTable>
                    <thead><tr>
                      <RTh style={{ width: 36 }}>#</RTh>
                      <RTh>Name</RTh>
                      <RTh style={{ width: 130 }}>Type</RTh>
                      <RTh style={{ width: 130, textAlign: 'right' }}>Value</RTh>
                      <RTh style={{ width: 130, textAlign: 'right' }}>Computed</RTh>
                      <RTh style={{ minWidth: 240 }}>Payable Account (Cr)</RTh>
                      <RTh style={{ width: 40 }}></RTh>
                    </tr></thead>
                    <tbody>
                      {deductions.map((d, i) => {
                        const computed = d.amountType === 'percent' ? (Number(formData.basicSalary) || 0) * (Number(d.value) || 0) / 100 : Number(d.value) || 0;
                        return (
                          <tr key={i}>
                            <RTd>{i + 1}</RTd>
                            <RTd><RInp value={d.name} onChange={ev => updateDeduction(i, { name: ev.target.value })} placeholder="EOBI, Income Tax..." /></RTd>
                            <RTd><RSel value={d.amountType} onChange={ev => updateDeduction(i, { amountType: ev.target.value })}>
                              <option value="fixed">Fixed Rs</option>
                              <option value="percent">% of Basic</option>
                            </RSel></RTd>
                            <RTd><RInp type="number" min="0" step="0.01" value={d.value} onChange={ev => updateDeduction(i, { value: ev.target.value })} style={{ textAlign: 'right' }} /></RTd>
                            <RTd style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(computed)}</RTd>
                            <NoClipTd>
                              <AccountSelect
                                companyId={activeCompany?.id}
                                value={d.accountId}
                                onChange={(account) => handleDeductionAccount(i, account)}
                                label="Payable A/c"
                              />
                            </NoClipTd>
                            <RTd style={{ textAlign: 'center' }}><RmBtn onClick={() => removeDeduction(i)}><FiX /></RmBtn></RTd>
                          </tr>
                        );
                      })}
                    </tbody>
                  </RowTable>
                </RowTableWrapWide>
              )}

              {/* Live preview */}
              <PreviewBar>
                <PreviewItem><span>Basic</span><strong>{formatCurrency(Number(formData.basicSalary) || 0)}</strong></PreviewItem>
                <PreviewItem><span>+ Allowances</span><strong>{formatCurrency(computeGross() - (Number(formData.basicSalary) || 0))}</strong></PreviewItem>
                <PreviewItem $highlight><span>= Gross Salary</span><strong>{formatCurrency(computeGross())}</strong></PreviewItem>
                <PreviewItem><span>− Deductions</span><strong>{formatCurrency(computeDeductions())}</strong></PreviewItem>
                <PreviewItem $highlight><span>= Net Salary</span><strong>{formatCurrency(computeGross() - computeDeductions())}</strong></PreviewItem>
              </PreviewBar>

              <SectionTitle>Notes</SectionTitle>
              <FormGroup $full>
                <TextArea rows={2} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
              </FormGroup>

              <ModalActions>
                <CancelButton onClick={closeModal}>Cancel</CancelButton>
                <SaveButton onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : (editing ? 'Update Staff' : 'Save Staff')}
                </SaveButton>
              </ModalActions>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// ═══════════════════════════════════════════════════════════
// Styled Components — match Banks/Customers pattern
// ═══════════════════════════════════════════════════════════

const SearchBar = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.border.light};
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 240px;
  max-width: 400px;

  svg {
    position: absolute;
    left: 12px;
    color: ${colors.text.tertiary};
  }

  input {
    width: 100%;
    padding: 10px 10px 10px 40px;
    border: 1px solid ${colors.border.light};
    border-radius: ${borderRadius.md};
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
    }
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: ${colors.background.paper};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  border-bottom: 1px solid ${colors.border.light};
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:hover { background: ${colors.background.paper}; }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  border-bottom: 1px solid ${colors.border.light};
`;

const StaffInfo = styled.div`display: flex; flex-direction: column;`;
const StaffName = styled.span`font-weight: 500;`;
const SubText = styled.span`font-size: 12px; color: ${colors.text.tertiary};`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: ${borderRadius.xl};
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? colors.status.success + '20' : colors.neutral[200]};
  color: ${props => props.$active ? colors.status.success : colors.text.tertiary};
`;

const EmpBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'Permanent': return colors.status.success + '20';
      case 'Probation': return colors.status.warning + '20';
      case 'Contract': return colors.status.info + '20';
      case 'Consultant': return colors.secondary.main + '20';
      default: return colors.neutral[200];
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'Permanent': return colors.status.success;
      case 'Probation': return colors.status.warning;
      case 'Contract': return colors.status.info;
      case 'Consultant': return colors.secondary.main;
      default: return colors.text.secondary;
    }
  }};
`;

const ActionButtons = styled.div`display: flex; gap: 8px;`;
const IconButton = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  background: ${props => props.$danger ? colors.status.error + '10' : colors.background.paper};
  color: ${props => props.$danger ? colors.status.error : colors.text.secondary};
  border: none; border-radius: ${borderRadius.sm};
  cursor: pointer; transition: all 0.2s;
  &:hover {
    background: ${props => props.$danger ? colors.status.error + '20' : colors.primary.main + '10'};
    color: ${props => props.$danger ? colors.status.error : colors.primary.main};
  }
`;

const LoadingState = styled.div`
  display: flex; align-items: center; justify-content: center;
  padding: 60px; color: ${colors.text.secondary};
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 60px; color: ${colors.text.tertiary};
  svg { font-size: 48px; margin-bottom: 16px; opacity: 0.5; }
  p { font-size: 16px; margin-bottom: 8px; }
  small { font-size: 13px; }
`;

// ─── Modal ───
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  width: 100%; max-width: 1000px;
  max-height: 92vh; overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.border.light};
`;

const ModalTitle = styled.h3`
  display: flex; align-items: center; gap: 10px;
  font-size: 18px; font-weight: 600;
  color: ${colors.text.primary};
  svg { color: ${colors.primary.main}; }
`;

const CloseButton = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  background: transparent; border: none;
  color: ${colors.text.tertiary}; cursor: pointer;
  border-radius: ${borderRadius.sm};
  &:hover { background: ${colors.background.paper}; color: ${colors.text.primary}; }
`;

const ModalBody = styled.div`padding: 24px;`;

const SectionTitle = styled.h4`
  font-size: 14px; font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px; margin-top: 24px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.border.light};
  &:first-child { margin-top: 0; }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  grid-column: ${props => props.$full ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  font-size: 13px; font-weight: 500;
  color: ${colors.text.primary};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  transition: all 0.2s;
  &:focus {
    outline: none; border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}20;
  }
  &::placeholder { color: ${colors.text.tertiary}; }
  &:disabled { background: ${colors.background.paper}; color: ${colors.text.tertiary}; }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const CheckboxWrapper = styled.label`
  display: flex; align-items: center; gap: 8px;
  cursor: pointer; font-size: 14px;
  color: ${colors.text.secondary};
  input[type="checkbox"] { width: 18px; height: 18px; cursor: pointer; }
`;

const ModalActions = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  margin-top: 24px; padding-top: 20px;
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px; font-weight: 500;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: ${colors.background.paper}; }
`;

const SaveButton = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: ${borderRadius.md};
  font-size: 14px; font-weight: 500;
  cursor: pointer; transition: all 0.2s;
  &:hover:not(:disabled) { background: ${colors.primary.dark}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// ─── Sub-tables for earnings/deductions in modal ───
const SubHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin: 16px 0 8px;
`;
const SubLabel = styled.div`
  font-size: 13px; font-weight: 600;
  color: ${colors.text.primary};
`;
const SubAddBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 6px 12px;
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: ${borderRadius.sm};
  font-size: 12px; font-weight: 500; cursor: pointer;
  &:hover { background: ${colors.primary.dark}; }
`;
const EmptyHint = styled.div`
  padding: 16px; text-align: center;
  border: 1px dashed ${colors.border.light};
  border-radius: ${borderRadius.md};
  color: ${colors.text.tertiary}; font-size: 13px;
`;
const RowTableWrap = styled.div`
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  overflow: hidden;
`;
const RowTableWrapWide = styled.div`
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  overflow: visible;
`;
const RowTable = styled.table`width: 100%; border-collapse: collapse;`;
const RTh = styled.th`
  padding: 8px 10px;
  background: ${colors.background.paper};
  color: ${colors.text.secondary};
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; text-align: left;
  border-bottom: 1px solid ${colors.border.light};
`;
const RTd = styled.td`
  padding: 6px 10px;
  font-size: 13px;
  border-bottom: 1px solid ${colors.border.light};
  vertical-align: middle;
  &:last-child { border-right: none; }
`;
const NoClipTd = styled(RTd)`overflow: visible; position: relative; z-index: 1;`;
const RInp = styled.input`
  width: 100%; padding: 6px 10px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: 13px;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const RSel = styled.select`
  width: 100%; padding: 6px 10px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: 13px; background: white;
`;
const RmBtn = styled.button`
  width: 28px; height: 28px;
  background: transparent; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  color: ${colors.status.error}; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: ${colors.status.error}; color: white; border-color: transparent; }
`;

const PreviewBar = styled.div`
  margin-top: 16px;
  display: flex; gap: 12px; flex-wrap: wrap;
  padding: 16px;
  background: ${colors.background.paper};
  border-radius: ${borderRadius.md};
`;
const PreviewItem = styled.div`
  flex: 1; min-width: 140px;
  padding: 10px 14px;
  background: ${props => props.$highlight ? colors.primary.main : 'white'};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  border-radius: ${borderRadius.md};
  display: flex; flex-direction: column; gap: 4px;
  span { font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; }
  strong { font-size: 16px; font-family: monospace; font-weight: 600; }
`;

export default SalaryStaff;
